import { useEffect, useState } from "react";
import AppsSideBarFolderItem from "./SideBarFolderItem";
import AppList from "@lib/components/AppComponents/AppList";
import ListEmptyResult from "@lib/components/AppComponents/AppList/ListEmptyResult";
import SidebarPlaceholder from "@lib/components/AppComponents/AppSkeleton/SidebarListSkeleton";
import {
  StyledSidebarContent,
  StyledSidebarFooter,
  StyledSidebarHeader,
  StyledSidebarList,
  StyledSidebarScrollbar,
} from "./index.styled";
import { PaginationType, UseQueryProps } from "@lib/types/Api";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { useNavigate, useParams } from "react-router-dom";
import { Tag, Typography } from "antd";
import { red } from "@ant-design/colors";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import { useSidebar } from "@lib/context/SidebarContext";
import { AppContext } from "@lib/types";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { getWalletLinkNoFilter } from "./utils";
import { AIWalletModal } from "@lib/components/PageComponents/Wallet/WalletModal";
import { AIWallet } from "@lib/types/Wallet";
import { useMutation } from "@tanstack/react-query";
import {
  apiAddAIWallet,
  apiDeleteAIWallet,
  apiUpdateAIWallet,
} from "@lib/api/walletAI";
import { toast } from "react-toastify";

type Props = {
  aiWalletsQuery: UseQueryProps<PaginationType<AIWallet>>;
  context: AppContext;
  customerId?: string;
};

const Sidebar: React.FC<Props> = ({ aiWalletsQuery, context, customerId }) => {
  const { messages } = useIntl();
  const { walletId } = useParams();
  const navigate = useNavigate();
  const [openModal, setModalOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<AIWallet>();
  const { collapsed } = useSidebar();

  useEffect(() => {
    if (!openModal) setSelectedWallet(null);
  }, [openModal]);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };
  const handleSelectWallet = (wallet: AIWallet) => {
    setSelectedWallet(wallet);
    toggle(true);
  };

  // const {
  //   addSendByteWalletMutation,
  //   updateSendByteWalletMutation,
  //   deleteSendByteWalletMutation,
  // } = useSendByteWalletQueries({
  //   messages,
  // });
  const { data, isLoading, isFetching, refetch } = aiWalletsQuery;
  const apiData = data?.data.data;

  const addAIMutation = useMutation({
    mutationFn: apiAddAIWallet,
    onSuccess() {
      toast.success(
        (messages["common.createdMessage"] as string).replace(
          "{name}",
          messages["common.wallet"] as string,
        ),
      );
      refetch();
    },
  });

  const updateAIMutation = useMutation({
    mutationFn: apiUpdateAIWallet,
    onSuccess() {
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["common.wallet"] as string,
        ),
      );
      refetch();
    },
  });

  const deleteAIMutation = useMutation({
    mutationFn: apiDeleteAIWallet,
    onSuccess() {
      toast.success(
        (messages["common.deletedMessage"] as string).replace(
          "{name}",
          messages["common.wallet"] as string,
        ),
      );
      refetch();
    },
  });

  useEffect(() => {
    if (
      addAIMutation.isSuccess ||
      updateAIMutation.isSuccess ||
      deleteAIMutation.isSuccess
    ) {
      toggle(false);
    }
  }, [
    addAIMutation.isSuccess,
    updateAIMutation.isSuccess,
    deleteAIMutation.isSuccess,
  ]);

  return (
    <>
      <StyledSidebarHeader>
        <AIWalletModal
          mutationQuery={selectedWallet ? updateAIMutation : addAIMutation}
          walletQuery={aiWalletsQuery}
          openModal={openModal}
          toggleModal={toggle}
          wallet={selectedWallet}
        />

        <StyledFlex
          style={{
            flexDirection: collapsed ? "column" : "row",
            justifyContent: "space-between",
          }}
        >
          <div>
            {walletId ? (
              <Tag
                closable
                color={red[4]}
                onClose={() => {
                  navigate(getWalletLinkNoFilter(context));
                }}
              >
                {collapsed
                  ? (messages["common.filter"] as string)
                  : (messages["common.removeFilter"] as string)}
              </Tag>
            ) : (
              <>
                {!collapsed && (
                  <Typography.Text>
                    {messages["common.wallets"] as string}
                  </Typography.Text>
                )}
              </>
            )}
          </div>

          <StyledFlex style={{ justifyContent: "flex-end", marginRight: 0 }}>
            <ActionButton
              type="add"
              noLabel
              noBg
              onClick={() => toggle(true)}
            />

            <ActionButton
              type="refresh"
              noLabel
              onClick={() => refetch()}
              disabled={isFetching}
              loading={isFetching}
            />
          </StyledFlex>
        </StyledFlex>
      </StyledSidebarHeader>

      <StyledSidebarScrollbar>
        <StyledSidebarContent>
          <StyledSidebarList
            aria-label="wallet list"
            loading={isLoading || isFetching}
          >
            <AppList
              data={apiData}
              ListEmptyComponent={
                <ListEmptyResult
                  loading={isLoading || isFetching}
                  placeholder={<SidebarPlaceholder />}
                />
              }
              renderItem={(item) => (
                <AppsSideBarFolderItem
                  key={item.id}
                  item={item}
                  deleteQuery={deleteAIMutation}
                  handleSelectWallet={handleSelectWallet}
                  sidebarCollapsed={collapsed}
                  context={context}
                  customerId={customerId}
                  walletQuery={aiWalletsQuery}
                />
              )}
            />
          </StyledSidebarList>
        </StyledSidebarContent>
      </StyledSidebarScrollbar>

      <StyledSidebarFooter
        message={
          !collapsed ? (
            <FormattedMessage
              id={"common.totalWalletCount"}
              values={{ total: data?.data?.total }}
            />
          ) : (
            <>
              <FormattedMessage id={"common.total"} /> {data?.data?.total}
            </>
          )
        }
      />
    </>
  );
};

export default Sidebar;
